import { fromEUR, fxBasis, paymentWithin, toEUR, type FxBasis } from '@/lib/envelopeFx';
import { fromMinor, toMinor } from '@/lib/money';
import type {
  FinanceEnvelopeHint as GeneratedEnvelopeHint,
  FinanceEnvelopeHintRequest,
  FinanceEnvelopeTag,
  FinanceMovementEnvelopeMove,
} from '@/api/generated/types.gen';

/**
 * What the form says when an older API answers without envelope_moves /
 * envelopes (envelopes spec §8): editing is unavailable, never «no tag».
 */
export const OLD_API_ENVELOPES =
  'The server runs an older version — editing payments with envelopes is unavailable until it is updated';

/**
 * The envelope hint as the server sends it. api/openapi.yaml marks
 * envelope_id and available_eur_minor nullable (no line names an active
 * envelope; an income has no «available»), but the generated client drops
 * the null arm — the same quirk the rawBody helper works around. Every
 * consumer reads this widened shape so a null is handled, not assumed away.
 */
export type FinanceEnvelopeHint = Omit<GeneratedEnvelopeHint, 'envelope_id' | 'available_eur_minor'> & {
  envelope_id: string | null;
  available_eur_minor: number | null;
};

/**
 * The amount of an operator's tag, by where it came from (OH-209). The owner's
 * rule: once the payment's currency is chosen, every amount on the form is
 * typed in it, and the system converts to EUR itself.
 *  - eur: an amount that came as EUR — a replacement's copy of the original's
 *    move, or an automatic tag picked again. It is sent as it is while it is
 *    untouched, and shown in the payment's currency through the conversion pair.
 *  - typed: the operator's text, in the PAYMENT's currency. It is read again
 *    whenever the currency changes (JPY has no decimals) and converted at the
 *    pair of the current input.
 *  - default: nothing typed yet — the spec §2 default (defaultPayment), which
 *    follows the form (header, lines, availability) until the operator types.
 */
export type ManualAmount =
  | { unit: 'eur'; minor: number }
  | { unit: 'typed'; text: string }
  | { unit: 'default' };

/**
 * The payment's envelope tag as the form holds it (envelopes spec §8):
 * none; auto — the server's suggestion, in EUR, replaced or cleared by the
 * next one; manual — the operator's choice (or a replacement's copy), never
 * touched by a suggestion and validated on its own. Every kind but none sends
 * an explicit amount: an empty amount never means «the whole payment».
 */
export type EnvelopeTagState =
  | { kind: 'none' }
  | { kind: 'auto'; envelopeId: string; amountMinor: number }
  | { kind: 'manual'; envelopeId: string; amount: ManualAmount };

export interface TagContext {
  /** The payment's currency; '' while none is chosen. */
  currency: string;
  /** The header in `currency` (minor units); null while it is not readable. */
  headerMinor: number | null;
  /**
   * The conversion pair of the CURRENT input (rateBasis); null while there is
   * none — then nothing converts. A pair of another currency counts as none.
   */
  basis: FxBasis | null;
  /** In EUR. The hint's (auto) or the list's available_on (manual); null while unknown. */
  availableMinor: number | null;
  /** A replacement's tag: availability is information, not a block. */
  replacement: boolean;
  /**
   * Σ of the form's lines (in `currency`) whose article defaults to this
   * envelope (spec §2.1) — what the default amount is made of.
   */
  lineSumFor: (envelopeId: string) => number;
  activeIds: ReadonlySet<string>;
  nameOf: (id: string) => string;
  occurredOn: string;
}

/** What the amount of a tag is read from; the rest of TagContext only judges it. */
export type AmountContext = Pick<
  TagContext,
  'currency' | 'headerMinor' | 'basis' | 'availableMinor' | 'replacement' | 'lineSumFor'
>;

type TaggedState = Exclude<EnvelopeTagState, { kind: 'none' }>;

export function applyHint(
  state: EnvelopeTagState,
  hint: FinanceEnvelopeHint | null,
  dismissed: boolean,
): EnvelopeTagState {
  if (state.kind === 'manual') return state;
  if (dismissed || !hint || !hint.envelope_id || hint.suggested_eur_minor <= 0) return { kind: 'none' };
  return { kind: 'auto', envelopeId: hint.envelope_id, amountMinor: hint.suggested_eur_minor };
}

/** A hint naming an envelope with nothing to take sets no tag — and says why. */
export function emptyHintNotice(
  hint: FinanceEnvelopeHint | null,
  nameOf: (id: string) => string,
): string | null {
  if (!hint?.envelope_id || hint.suggested_eur_minor > 0) return null;
  return `Envelope ${nameOf(hint.envelope_id)} is empty — paid from operations`;
}

// The pair, if it is the pair of THIS payment's currency: the form changes
// currency before the hint for the new one arrives.
const basisOf = (c: AmountContext): FxBasis | null =>
  c.basis !== null && c.basis.currency === c.currency ? c.basis : null;

/**
 * The amount a pick starts on (spec §2, in the payment's currency): the lines
 * whose articles name the envelope, or the whole header when none does — never
 * more than the header. A spend also takes no more than the envelope has: the
 * payment-currency figure of the available. An EMPTY envelope leaves it
 * uncapped (a cap of 0 would read as «enter an amount»), so the availability
 * problem names the envelope instead; a replacement is never capped (EP-02).
 * Null while the header is not readable.
 */
export function defaultPayment(envelopeId: string, c: AmountContext): number | null {
  const header = c.headerMinor;
  if (header === null || header <= 0) return null;
  const lines = c.lineSumFor(envelopeId);
  const wanted = lines > 0 ? Math.min(lines, header) : header;
  const basis = basisOf(c);
  if (c.replacement || c.availableMinor === null || basis === null) return wanted;
  const cap = paymentWithin(c.availableMinor, basis);
  return cap >= 1 ? Math.min(wanted, cap) : wanted;
}

/**
 * A tag's amount in both currencies. `payment` is in the payment's currency,
 * `eur` is what goes on the wire; either is null where it cannot be known:
 *  - an amount that came as EUR (auto, manual `eur`) IS its EUR; its payment
 *    figure needs the pair;
 *  - typed text is read in the CURRENT currency, and has an EUR only while it
 *    is positive and there is a pair;
 *  - the default has the payment figure as soon as the header is readable.
 */
export function resolveAmount(
  state: TaggedState,
  c: AmountContext,
): { payment: number | null; eur: number | null } {
  const basis = basisOf(c);
  const fromEur = (eur: number) => ({ payment: basis ? fromEUR(eur, basis) : null, eur });
  const fromPayment = (payment: number | null) => ({
    payment,
    eur: payment !== null && payment > 0 && basis ? toEUR(payment, basis) : null,
  });
  if (state.kind === 'auto') return fromEur(state.amountMinor);
  switch (state.amount.unit) {
    case 'eur':
      return fromEur(state.amount.minor);
    case 'typed':
      return fromPayment(c.currency ? toMinor(state.amount.text, c.currency) : null);
    case 'default':
      return fromPayment(defaultPayment(state.envelopeId, c));
  }
}

/**
 * What the envelope amount field shows: the operator's own text as typed,
 * otherwise the amount in the payment's currency — or nothing while it
 * cannot be said (an amount in EUR with no pair). Never a number in the
 * wrong unit.
 */
export function amountText(state: TaggedState, c: AmountContext): string {
  if (state.kind === 'manual' && state.amount.unit === 'typed') return state.amount.text;
  const { payment } = resolveAmount(state, c);
  return payment === null ? '' : fromMinor(payment, c.currency);
}

const AT_LEAST = 'Envelope amount must be at least 0.01.';
// Said of an AMOUNT that converts to 0.00 EUR, which no tag can be. (The form
// says its own words about a PAYMENT that does: no pair at all.)
const LESS_THAN_A_CENT = 'Envelope amount converts to less than 0.01 EUR.';
const aboveMessage = (headerMinor: number, currency: string) =>
  `Envelope amount is above the payment's ${fromMinor(headerMinor, currency)} ${currency}.`;

export function tagProblems(state: EnvelopeTagState, c: TagContext): string[] {
  if (state.kind === 'none') return [];
  const out: string[] = [];
  if (!c.activeIds.has(state.envelopeId)) out.push('The chosen envelope is switched off; pick another or none.');
  const basis = basisOf(c);
  const { payment, eur } = resolveAmount(state, c);
  if (state.kind === 'auto' || state.amount.unit === 'eur') {
    // Came as EUR: checked as EUR, against the EUR side of the pair.
    if (eur === null || eur <= 0) {
      out.push(AT_LEAST);
      return out;
    }
    if (basis !== null && eur > basis.headerEURMinor) out.push(aboveMessage(basis.headerMinor, c.currency));
  } else {
    // The operator's figure (typed, or the default), in the payment's currency.
    // An unreadable header leaves the default without an amount: the header's
    // own problem speaks.
    if (payment === null && state.amount.unit === 'default') return out;
    if (payment === null || payment <= 0) {
      out.push(AT_LEAST);
      return out;
    }
    if (c.headerMinor !== null && payment > c.headerMinor) out.push(aboveMessage(c.headerMinor, c.currency));
    if (eur !== null && eur < 1) out.push(LESS_THAN_A_CENT);
  }
  // A replacement is one operation with its mirror, which may put the money
  // back into this envelope; the server checks the whole plan (envelopes
  // spec §4).
  if (!c.replacement && c.availableMinor !== null && eur !== null && eur > c.availableMinor) {
    const name = c.nameOf(state.envelopeId);
    const available = fromMinor(c.availableMinor, 'EUR');
    out.push(
      basis !== null && c.currency !== 'EUR'
        ? `${name} has only ${fromMinor(paymentWithin(c.availableMinor, basis), c.currency)} ${c.currency} (${available} EUR) available on ${c.occurredOn}.`
        : `${name} has only ${available} EUR available on ${c.occurredOn}.`,
    );
  }
  return out;
}

/**
 * What goes on the wire: the tag's EUR, and only a whole cent of it. A tag
 * without one yields nothing, but it never gets that far: tagProblems keeps
 * the submit disabled, and the form refuses to send a chosen tag it could
 * not convert.
 */
export function tagToRequest(state: EnvelopeTagState, c: AmountContext): FinanceEnvelopeTag | undefined {
  if (state.kind === 'none') return undefined;
  const { eur } = resolveAmount(state, c);
  if (eur === null || eur < 1) return undefined;
  return { envelope_id: state.envelopeId, amount_eur_minor: eur };
}

// --- The draft bound to its input ------------------------------------------

export type HintStatus =
  | { kind: 'idle' }
  | { kind: 'debouncing'; forKey: string }
  | { kind: 'loading'; forKey: string }
  | { kind: 'ready'; forKey: string; hint: FinanceEnvelopeHint }
  /** `message` is the server's reason (e.g. no exchange rate for the date), shown next to Retry. */
  | { kind: 'error'; forKey: string; message: string };

/**
 * The envelope tag bound to the input it was computed for. `status` is the
 * hint request's own state, kept apart from the tag. `acceptedKey` names the
 * input whose answer the current auto tag (or a hint-made «none») came from,
 * and `accepted` is that answer itself — kept here rather than read off
 * `status`, which moves on as soon as the input does: leaving the input and
 * coming back to it must not mistake the very answer that was accepted for a
 * changed one.
 *
 * An auto tag (and an open «none») is FROZEN on its accepted answer once
 * accepted (EP-R2-02): `changed` is a later, different answer for that same
 * input, offered through «Apply». A manual tag and an explicit «None» have no
 * suggestion to protect and are offered nothing: they convert at the NEWEST
 * answer for the input, so a rate corrected while the form is open reaches the
 * amount before it is sent once the server answers the input again (an
 * invalidation after a finance write in this tab, or a return to the input) —
 * in either order of answer and tag. An answer that comes AFTER the tag became
 * the operator's replaces `accepted` (and the ready `status.hint`) on the spot.
 * One that came BEFORE it, while the tag was still automatic or open and
 * frozen, was kept aside as `latest`; the pick, the typing or the explicit
 * «None» that makes the tag the operator's adopts it.
 */
export interface EnvelopeDraft {
  tag: EnvelopeTagState;
  /**
   * An explicit «None» — the operator's, or the start of a replacement of a
   * known untagged payment (startDraft). No hint sets a tag over it; picking
   * an envelope lifts it.
   */
  dismissed: boolean;
  status: HintStatus;
  acceptedKey: string | null;
  accepted: FinanceEnvelopeHint | null;
  /**
   * The NEWEST answer for `acceptedKey`, whatever the tag did with it. An auto
   * tag (or an open «none») stays frozen on `accepted`: a later different answer
   * goes to `changed` and a later answer with the same suggestion is dropped, so
   * the newest would be lost — and with it the rate a manual tag born next must
   * convert at. `latest` keeps it; the pick, the typing or the explicit «None»
   * that makes the tag the operator's adopts it as `accepted` (and as the ready
   * `status.hint`). For a manual tag and an explicit «None» it IS `accepted`.
   */
  latest: FinanceEnvelopeHint | null;
  /**
   * The change offered through «Apply»: while one is offered it IS `latest`, the
   * newest answer for the input. The offered suggestion answered again at
   * another rate replaces it, so the figure offered and the pair «Apply» adopts
   * are the newest.
   */
  changed: FinanceEnvelopeHint | null;
}

export const EMPTY_DRAFT: EnvelopeDraft = {
  tag: { kind: 'none' },
  dismissed: false,
  status: { kind: 'idle' },
  acceptedKey: null,
  accepted: null,
  latest: null,
  changed: null,
};

export type DraftEvent =
  | { type: 'input'; key: string | null }
  | { type: 'request'; key: string; current: string | null }
  | { type: 'result'; key: string; hint: FinanceEnvelopeHint; current: string | null }
  | { type: 'failure'; key: string; current: string | null; message: string }
  | { type: 'apply' }
  /**
   * envelopeId '' = «None». Any other pick starts on the spec §2 default,
   * except that picking the envelope the tag already has keeps its amount.
   */
  | { type: 'pick'; envelopeId: string }
  /** The operator typed the envelope amount, in the payment's currency. */
  | { type: 'amount'; text: string }
  /**
   * The form's Direction changed: the draft is replaced whole with `to`
   * (startDraft). Nothing of the old one — a pick, a «None», an accepted
   * hint — survives.
   */
  | { type: 'reset'; to: EnvelopeDraft };

/** A stable key of the form's current hint input: fields in a fixed order, lines in form order. */
export function hintKeyOf(body: FinanceEnvelopeHintRequest | null): string | null {
  if (!body) return null;
  return JSON.stringify([
    body.direction,
    body.currency,
    body.occurred_on,
    body.amount_minor,
    body.lines.map((l) => [l.category_id, l.amount_minor]),
  ]);
}

/**
 * The conversion pair of the input the hint answered: a EUR payment converts
 * 1:1 and needs no hint; any other currency pairs its header with the hint's
 * header_eur_minor — the rate the server converts the payment with. The form
 * passes the hint READY FOR THE CURRENT KEY; for a manual tag (and an explicit
 * «None») that is the NEWEST answer for the input, in either order of answer and
 * tag: an answer that comes after the tag became the operator's replaces the
 * accepted one, and one that came before it (kept aside as `latest` while the
 * tag was automatic or open) is adopted by the pick or the typing that makes the
 * tag manual — so a rate corrected while the form is open reaches the amount
 * before it is sent once the server answers the input again (an invalidation
 * after a finance write in this tab, or a return to the input), instead of the
 * amount going out at the first rate. An auto tag stays on the answer it was
 * accepted from: its EUR is sent as it is; the pair only shows it and checks it
 * against the header.
 */
export function rateBasis(
  currency: string,
  headerMinor: number | null,
  hint: FinanceEnvelopeHint | null,
): FxBasis | null {
  if (currency === 'EUR') return fxBasis('EUR', headerMinor, headerMinor);
  return hint ? fxBasis(currency, headerMinor, hint.header_eur_minor) : null;
}

/**
 * The pair the ACCEPTED answer was computed for: its currency and header are
 * read back from the key it was accepted under (hintKeyOf's layout), its EUR
 * from the answer. So an amount that came in EUR keeps showing against the
 * pair it was computed for while the next answer is on its way, instead of
 * blanking on every keystroke. Null for no accepted answer, or for a key that
 * is not a hint key.
 */
export function acceptedBasis(d: EnvelopeDraft): FxBasis | null {
  if (d.acceptedKey === null || d.accepted === null) return null;
  try {
    const key: unknown = JSON.parse(d.acceptedKey);
    if (!Array.isArray(key)) return null;
    const currency: unknown = key[1];
    const headerMinor: unknown = key[3];
    if (typeof currency !== 'string' || typeof headerMinor !== 'number') return null;
    return fxBasis(currency, headerMinor, d.accepted.header_eur_minor);
  } catch {
    return null;
  }
}

const sameHint = (a: FinanceEnvelopeHint | null, b: FinanceEnvelopeHint) =>
  !!a && a.envelope_id === b.envelope_id && a.suggested_eur_minor === b.suggested_eur_minor;

export function reduceDraft(d: EnvelopeDraft, e: DraftEvent): EnvelopeDraft {
  switch (e.type) {
    // A new input withdraws an offered change: «Apply» belongs to the input
    // that was answered, not to the one being typed.
    case 'input':
      if (e.key === null) return d.status.kind === 'idle' ? d : { ...d, status: { kind: 'idle' }, changed: null };
      if (d.status.kind !== 'idle' && d.status.forKey === e.key) return d;
      return { ...d, status: { kind: 'debouncing', forKey: e.key }, changed: null };
    case 'request':
      if (e.key !== e.current) return d;
      return { ...d, status: { kind: 'loading', forKey: e.key } };
    case 'failure':
      if (e.key !== e.current) return d;
      // A failed re-ask for the accepted input (an invalidation refetch, a
      // return to it) leaves the accepted answer standing — the frozen one of an
      // auto tag, the newest one a manual tag has had.
      if (d.acceptedKey === e.key && d.accepted) {
        return { ...d, status: { kind: 'ready', forKey: e.key, hint: d.accepted } };
      }
      return { ...d, status: { kind: 'error', forKey: e.key, message: e.message } }; // the tag stays as it is
    case 'result': {
      if (e.key !== e.current) return d; // a slow answer for an older input
      if (d.acceptedKey === e.key && d.accepted) {
        // The same input answered again (an invalidation after a finance write,
        // a return to it). Whatever the tag does with it, it is the newest
        // answer for the input: `latest`.
        //
        // A manual tag and an explicit «None» have no suggestion to freeze and
        // are offered nothing — a hint changes neither. The newest answer is the
        // accepted one: a manual amount converts at the pair of the input as the
        // server answers it now (a rate corrected by hand while the form is open
        // must reach the amount before it is sent). The very same answer again
        // changes nothing, and the same state comes back.
        if (d.tag.kind === 'manual' || d.dismissed) {
          const newest: HintStatus = { kind: 'ready', forKey: e.key, hint: e.hint };
          return sameStatus(d, newest) && d.accepted === e.hint && d.latest === e.hint && !d.changed
            ? d
            : { ...d, status: newest, accepted: e.hint, latest: e.hint, changed: null };
        }
        // An auto tag (and an open «none») stands on the answer it was accepted
        // from; a later different one is offered through «Apply». The newest is
        // kept aside all the same, for the pick, the typing or the «None» that
        // makes the tag the operator's (adoptLatest).
        const ready: HintStatus = { kind: 'ready', forKey: e.key, hint: d.accepted };
        const held = sameStatus(d, ready) && d.latest === e.hint; // the status and the newest are already as they would be
        const settled = { ...d, status: ready, latest: e.hint };
        if (sameHint(d.accepted, e.hint)) return held && !d.changed ? d : { ...settled, changed: null };
        // Any other answer is the offer — and the newest one: the offered
        // suggestion answered again at another rate replaces the offer, so the
        // figure shown and the pair «Apply» adopts are the newest. Held only when
        // the offer already IS this very answer.
        return held && d.changed === e.hint ? d : { ...settled, changed: e.hint };
      }
      return {
        ...d,
        tag: applyHint(d.tag, e.hint, d.dismissed),
        status: { kind: 'ready', forKey: e.key, hint: e.hint },
        acceptedKey: e.key,
        accepted: e.hint,
        latest: e.hint,
        changed: null,
      };
    }
    // `accepted` becomes the offered answer. An auto tag's EUR is sent as it is;
    // the pair it is accepted on only SHOWS it and checks it against the header.
    // `latest` is left alone (the offer is the newest answer, so it is `latest`
    // already), and whatever makes the tag the operator's adopts it (adoptLatest).
    case 'apply': {
      if (!d.changed || d.tag.kind === 'manual') return { ...d, changed: null };
      const tag = applyHint({ kind: 'none' }, d.changed, false);
      const status: HintStatus =
        d.acceptedKey && d.status.kind === 'ready' && d.status.forKey === d.acceptedKey
          ? { kind: 'ready', forKey: d.acceptedKey, hint: d.changed }
          : d.status;
      return { ...d, tag, status, accepted: d.changed, changed: null, dismissed: false };
    }
    case 'pick':
      if (!e.envelopeId) return { ...d, ...adoptLatest(d), tag: { kind: 'none' }, dismissed: true, changed: null };
      return {
        ...d,
        ...adoptLatest(d),
        tag: { kind: 'manual', envelopeId: e.envelopeId, amount: pickedAmount(d.tag, e.envelopeId) },
        dismissed: false,
        changed: null,
      };
    case 'amount':
      if (d.tag.kind === 'none') return d;
      return {
        ...d,
        ...adoptLatest(d),
        tag: { kind: 'manual', envelopeId: d.tag.envelopeId, amount: { unit: 'typed', text: e.text } },
        changed: null,
      };
    case 'reset':
      return e.to;
  }
}

/**
 * Where a payment form's envelope side starts: on mount, and again on every
 * Direction change. A tag never outlives a flip — «from envelope A, 600»
 * would silently become «into envelope A, 600», an allocation nobody chose.
 *  - `copy`: a replacement's original carried one tag of its own, in the
 *    direction the form has now. The copy is the operator's choice (manual),
 *    never replaced by a hint, at the MOVE's amount — which may be less than
 *    the payment's. It stays in EUR, as the move recorded it, and is sent as
 *    it is while untouched; the form shows it in the payment's currency.
 *  - `knownEmpty`: the original is known to carry no tag. The replacement
 *    starts on an explicit «None», the state the operator's own «None»
 *    leaves, so no hint tags a payment that had no tag.
 *  - Otherwise empty and open to the hint (a new payment; a replacement that
 *    has to choose shows its choice instead).
 */
export function startDraft(
  copy: Pick<FinanceMovementEnvelopeMove, 'envelope_id' | 'amount_eur_minor'> | undefined,
  knownEmpty: boolean,
): EnvelopeDraft {
  if (copy) {
    return {
      ...EMPTY_DRAFT,
      tag: { kind: 'manual', envelopeId: copy.envelope_id, amount: { unit: 'eur', minor: copy.amount_eur_minor } },
    };
  }
  return knownEmpty ? { ...EMPTY_DRAFT, dismissed: true } : EMPTY_DRAFT;
}

// A tag that becomes the operator's — a pick, typing, an explicit «None» —
// converts at the NEWEST answer for its input, also when that answer came while
// the tag was still automatic or open and frozen: it is `latest`. It becomes the
// accepted answer, and a status that is ready for the accepted input follows it,
// so the pair the form reads (rateBasis) is the newest one.
function adoptLatest(d: EnvelopeDraft): Pick<EnvelopeDraft, 'accepted' | 'status'> {
  const accepted = d.latest ?? d.accepted;
  const s = d.status;
  if (accepted !== null && s.kind === 'ready' && s.forKey === d.acceptedKey && s.hint !== accepted) {
    return { accepted, status: { kind: 'ready', forKey: s.forKey, hint: accepted } };
  }
  return { accepted, status: s };
}

// What picking `envelopeId` leaves as the amount: the tag's own when it already
// names that envelope (an automatic one is frozen at the EUR it showed),
// otherwise the live default — the typed text of another envelope is not
// carried over.
function pickedAmount(tag: EnvelopeTagState, envelopeId: string): ManualAmount {
  if (tag.kind === 'none' || tag.envelopeId !== envelopeId) return { unit: 'default' };
  return tag.kind === 'auto' ? { unit: 'eur', minor: tag.amountMinor } : tag.amount;
}

function sameStatus(d: EnvelopeDraft, s: HintStatus): boolean {
  return d.status.kind === s.kind && d.status.kind === 'ready' && s.kind === 'ready' &&
    d.status.forKey === s.forKey && d.status.hint === s.hint;
}

/**
 * Send only what is confirmed for the CURRENT input — an explicit «None», a
 * manual tag in a EUR payment (it converts 1:1, so there is nothing to wait
 * for), or a ready answer for this very key. A manual tag in another currency
 * waits for the answer too, the copy of a replacement included: the pair its
 * amount converts with is that answer's header (rateBasis). An error never
 * reads as «no envelope». currentKey null passes here: a hidden block sends
 * no tag, and an incomplete input is held by the form's own checks (amounts,
 * articles, the line sum).
 */
export function submitGate(
  d: EnvelopeDraft,
  currentKey: string | null,
  currency: string,
): { ok: true } | { ok: false; reason: 'calculating' } | { ok: false; reason: 'error'; message: string } {
  if (d.dismissed || currentKey === null) return { ok: true };
  if (d.tag.kind === 'manual' && currency === 'EUR') return { ok: true };
  if (d.status.kind === 'ready' && d.status.forKey === currentKey) return { ok: true };
  if (d.status.kind === 'error' && d.status.forKey === currentKey) {
    return { ok: false, reason: 'error', message: d.status.message };
  }
  return { ok: false, reason: 'calculating' };
}
